<template>
  <v-dialog
    :model-value="modelValue"
    max-width="760"
    scrollable
    content-class="employee-condition-dialog"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <div class="employee-condition-modal">
      <header class="employee-condition-modal__head">
        <div class="employee-condition-modal__head-main">
          <div class="employee-condition-modal__head-icon">
            <v-icon size="20" color="white">mdi-text-box-check-outline</v-icon>
          </div>
          <div class="employee-condition-modal__head-text">
            <h3 class="employee-condition-modal__title">{{ employeeName }} 사원 멘트</h3>
            <p class="employee-condition-modal__subtitle">
              {{ yearMonthLabel }} · 적용 조건 및 산출 근거
            </p>
          </div>
        </div>
        <v-btn
          icon
          size="small"
          variant="text"
          class="employee-condition-modal__close"
          aria-label="닫기"
          @click="emit('update:modelValue', false)"
        >
          <v-icon size="18">mdi-close</v-icon>
        </v-btn>
      </header>

      <div class="employee-condition-modal__body">
        <LoadingOverlay :show="loading" text="불러오는 중..." variant="light" />

        <div v-if="!loading && sections.length === 0" class="employee-condition-modal__empty">
          <v-icon size="40" color="#94a3b8">mdi-information-outline</v-icon>
          <p>표시할 조건 정보가 없습니다.</p>
        </div>

        <section
          v-for="section in sections"
          :key="section.id"
          class="employee-condition-modal__section"
          :class="`employee-condition-modal__section--${section.type}`"
        >
          <header class="employee-condition-modal__section-head">
            <v-icon size="16" class="employee-condition-modal__section-icon">{{ section.icon }}</v-icon>
            <h4 class="employee-condition-modal__section-title">{{ section.title }}</h4>
            <span v-if="section.badge" class="employee-condition-modal__section-badge">{{ section.badge }}</span>
          </header>

          <!-- 요약: 키·값 그리드 -->
          <div v-if="section.type === 'summary' && section.kvItems?.length" class="employee-condition-modal__kv-grid">
            <div
              v-for="(item, idx) in section.kvItems"
              :key="`${section.id}-kv-${idx}`"
              class="employee-condition-modal__kv-item"
              :class="{ 'employee-condition-modal__kv-item--highlight': item.highlight }"
            >
              <span class="employee-condition-modal__kv-label">{{ item.label }}</span>
              <span class="employee-condition-modal__kv-value">{{ item.value }}</span>
            </div>
          </div>

          <!-- 설정: ON/OFF 칩 -->
          <div v-else-if="section.type === 'settings' && section.settingItems?.length" class="employee-condition-modal__settings">
            <div
              v-for="(item, idx) in section.settingItems"
              :key="`${section.id}-set-${idx}`"
              class="employee-condition-modal__setting-row"
            >
              <span
                class="employee-condition-modal__setting-chip"
                :class="item.active ? 'employee-condition-modal__setting-chip--on' : 'employee-condition-modal__setting-chip--off'"
              >
                {{ item.active ? '적용' : '미적용' }}
              </span>
              <div class="employee-condition-modal__setting-text">
                <span class="employee-condition-modal__setting-label">{{ item.label }}</span>
                <span v-if="item.description" class="employee-condition-modal__setting-desc">{{ item.description }}</span>
              </div>
            </div>
          </div>

          <!-- 안내/가족요양/모드 -->
          <div v-else-if="section.type === 'note' && section.noteLines?.length" class="employee-condition-modal__note">
            <p
              v-for="(line, idx) in section.noteLines"
              :key="`${section.id}-note-${idx}`"
              class="employee-condition-modal__note-line"
              :class="getNoteLineClass(line)"
            >
              <v-icon
                v-if="getNoteLineIcon(line)"
                size="14"
                class="employee-condition-modal__note-icon"
              >{{ getNoteLineIcon(line) }}</v-icon>
              <span>{{ formatNoteLine(line) }}</span>
            </p>
          </div>

          <!-- 경고 -->
          <div v-else-if="section.type === 'warning' && section.warningLines?.length" class="employee-condition-modal__warning">
            <div
              v-for="(line, idx) in section.warningLines"
              :key="`${section.id}-warn-${idx}`"
              class="employee-condition-modal__warning-item"
            >
              <v-icon size="16" color="#c62828">mdi-alert-circle-outline</v-icon>
              <span>{{ line }}</span>
            </div>
          </div>

          <!-- 항목별 산출 근거 -->
          <div v-else-if="section.type === 'fields' && section.fieldItems?.length" class="employee-condition-modal__fields">
            <article
              v-for="(item, idx) in section.fieldItems"
              :key="`${section.id}-field-${idx}`"
              class="employee-condition-modal__field-card"
            >
              <div class="employee-condition-modal__field-head">
                <span class="employee-condition-modal__field-label">{{ item.label }}</span>
                <span v-if="item.amount" class="employee-condition-modal__field-amount">{{ item.amount }}원</span>
              </div>
              <div
                v-if="getDetailLines(item.detail).length"
                class="employee-condition-modal__field-detail"
              >
                <p
                  v-for="(line, lineIdx) in getDetailLines(item.detail)"
                  :key="`${section.id}-field-${idx}-line-${lineIdx}`"
                  class="employee-condition-modal__field-line"
                  :class="getDetailLineClass(line)"
                >
                  {{ line }}
                </p>
              </div>
            </article>
          </div>
        </section>
      </div>

      <footer class="employee-condition-modal__footer">
        <v-btn variant="text" class="btn-close" @click="emit('update:modelValue', false)">
          닫기
        </v-btn>
      </footer>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
import LoadingOverlay from '@/components/common/LoadingOverlay.vue';

export interface HomeWageEmployeeConditionKvItem {
  label: string;
  value: string;
  highlight?: boolean;
}

export interface HomeWageEmployeeConditionSettingItem {
  label: string;
  active: boolean;
  description?: string;
}

export interface HomeWageEmployeeConditionFieldItem {
  label: string;
  amount?: string;
  detail: string;
}

export type HomeWageEmployeeConditionSectionType =
  | 'summary'
  | 'settings'
  | 'note'
  | 'warning'
  | 'fields';

export interface HomeWageEmployeeConditionSection {
  id: string;
  title: string;
  type: HomeWageEmployeeConditionSectionType;
  icon?: string;
  badge?: string;
  kvItems?: HomeWageEmployeeConditionKvItem[];
  settingItems?: HomeWageEmployeeConditionSettingItem[];
  fieldItems?: HomeWageEmployeeConditionFieldItem[];
  noteLines?: string[];
  warningLines?: string[];
}

withDefaults(
  defineProps<{
    modelValue?: boolean;
    employeeName?: string;
    yearMonthLabel?: string;
    sections?: HomeWageEmployeeConditionSection[];
    loading?: boolean;
  }>(),
  {
    modelValue: false,
    employeeName: '-',
    yearMonthLabel: '',
    sections: () => [],
    loading: false,
  }
);

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

const getDetailLines = (detail: string): string[] =>
  detail
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line && line !== '=');

const formatNoteLine = (line: string): string =>
  line.replace(/^[★⚠️✓•\-\s]+/, '').trim() || line;

const getNoteLineClass = (line: string): string => {
  if (line.startsWith('★') || line.startsWith('⚠')) return 'employee-condition-modal__note-line--emphasis';
  if (line.startsWith('✓')) return 'employee-condition-modal__note-line--success';
  if (line.startsWith('[') || line.startsWith('【')) return 'employee-condition-modal__note-line--heading';
  if (line.startsWith('•')) return 'employee-condition-modal__note-line--bullet';
  return '';
};

const getNoteLineIcon = (line: string): string | null => {
  if (line.startsWith('★')) return 'mdi-star-circle-outline';
  if (line.startsWith('⚠')) return 'mdi-alert-outline';
  if (line.startsWith('✓')) return 'mdi-check-circle-outline';
  return null;
};

const getDetailLineClass = (line: string): string => {
  if (line.startsWith('★') || line.startsWith('【') || line.startsWith('[')) {
    return 'employee-condition-modal__field-line--heading';
  }
  if (line.startsWith('조건:') || line.startsWith('선택:') || line.startsWith('고정:')) {
    return 'employee-condition-modal__field-line--meta';
  }
  if (/^=\s/.test(line)) {
    return 'employee-condition-modal__field-line--result';
  }
  if (line.includes('=') && /\d/.test(line)) {
    return 'employee-condition-modal__field-line--formula';
  }
  return '';
};
</script>
